import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { PasswordResetModal } from '../components/modals/PasswordResetModal';
import { Navbar } from '../components/Navbar';
import { ShieldCheck, AlertCircle } from 'lucide-react';
export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const isAdminEmail = email.toLowerCase().includes('admin');
  const handlePasswordReset = async (newPassword: string) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    setShowResetModal(false);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // For admin, pass empty string as password (not required)
      const user = await login(email, isAdminEmail ? '' : password);
      // Route based on role
      if (user.role === 'admin') {
        navigate('/');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };
  return <div className="min-h-screen bg-rose-50/30" data-id="element-2701">
      <Navbar data-id="element-2702" />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-80px)] flex items-center justify-center" data-id="element-2703">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full" data-id="element-2704">
          <div className="text-center mb-8" data-id="element-2705">
            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2" data-id="element-2706">
              Welcome Back
            </h1>
            <p className="text-gray-600" data-id="element-2707">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2708">
            <div className="space-y-2" data-id="element-2709">
              <Input label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" data-id="element-2710" />

              <AnimatePresence data-id="element-2711">
                {isAdminEmail && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="flex items-center gap-2 text-sm text-green-600 font-medium bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-2712">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0" data-id="element-2713" />
                    <span data-id="element-2714">
                      Admin account - Auto-login enabled (no password required)
                    </span>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <AnimatePresence mode="wait" data-id="element-2715">
              {!isAdminEmail && <motion.div key="password-field" initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="overflow-hidden" data-id="element-2716">
                  <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} required={!isAdminEmail} placeholder="••••••••" data-id="element-2717" />
                </motion.div>}
            </AnimatePresence>

            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm font-medium" data-id="element-2718">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-2719" />
                <span data-id="element-2720">{error}</span>
              </motion.div>}

            <Button type="submit" className="w-full" disabled={loading} data-id="element-2721">
              {loading ? 'Signing in...' : isAdminEmail ? 'Access Admin Dashboard' : 'Sign In'}
            </Button>

            <div className="text-center space-y-2" data-id="element-2722">
              {!isAdminEmail && <button type="button" onClick={() => setShowResetModal(true)} className="text-sm text-rose-500 hover:text-rose-600 font-medium block w-full" data-id="element-2723">
                  Forgot password?
                </button>}

              <p className="text-sm text-gray-600" data-id="element-2724">
                Don't have an account?{' '}
                <Link to="/signup" className="font-medium text-rose-500 hover:text-rose-600" data-id="element-2725">
                  Sign up
                </Link>
              </p>
            </div>
          </form>

          {/* Admin Login Helper */}
          {isAdminEmail && <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-2726">
              <p className="text-xs text-gray-500 text-center mb-2" data-id="element-2727">
                Admin Accounts:
              </p>
              <div className="space-y-1 text-xs text-gray-600" data-id="element-2728">
                <div className="bg-gray-50 p-2 rounded text-center" data-id="element-2729">
                  Admin@main.com
                </div>
                <div className="bg-gray-50 p-2 rounded text-center" data-id="element-2730">
                  Admin@limpopo.com
                </div>
                <div className="bg-gray-50 p-2 rounded text-center" data-id="element-2731">
                  Admin@Gauteng.com
                </div>
              </div>
            </div>}
        </motion.div>
      </div>

      <PasswordResetModal isOpen={showResetModal} onSubmit={handlePasswordReset} data-id="element-2732" />
    </div>;
};